// @flow
import React, { type ElementRef, Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Field,
  formValueSelector,
  reduxForm,
  type FormProps,
} from 'redux-form';

import type { Dispatch, FileType } from 'state/types';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import FormField from 'view/Forms/FormField';
import {
  actions as modalPromptActions,
  showSingleButtonAlert,
} from 'portfolio-shared/state/reducers/modalPromptReducer';
import { computeFileTypeName } from 'util/catalogUtils';

const { showModalPrompt } = modalPromptActions;

type Props = {
  extensions: ?string,
  fileType: ?FileType,
  fileTypeName: ?string,
  fileTypes: ?Array<FileType>,
  setFileTypes: Function,
  isAdd: boolean,
  onCancel: Function,
  showModalPrompt: Function,
  showSingleButtonAlert: Function,
  sharedState: ?Object,
  title: ?string,
} & FormProps;

type State = {};

class FileTypeEditor extends Component<Props, State> {
  handleCancelClicked: Function;
  handleAddOrUpdateClicked: Function;
  handleKeyDown: Function;
  resetAndCancel: Function;
  setFocusOnTitle: Function;
  titleField: ?ElementRef<typeof Field>;

  constructor() {
    super();
    this.setFocusOnTitle = this.setFocusOnTitle.bind(this);
    this.handleCancelClicked = this.handleCancelClicked.bind(this);
    this.resetAndCancel = this.resetAndCancel.bind(this);
    this.handleAddOrUpdateClicked = this.handleAddOrUpdateClicked.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    const { fileType, initialize } = this.props;
    initialize(fileTypeToFormValues(fileType));
  }

  componentWillReceiveProps(nextProps) {
    const { fileType, initialize, isAdd } = this.props;
    function makeId(ft: ?FileType) {
      return ft ? computeFileTypeName(ft).toLowerCase() : '';
    }
    if (makeId(fileType) !== makeId(nextProps.fileType)) {
      initialize(fileTypeToFormValues(nextProps.fileType));
    }
    if (!isAdd && nextProps.isAdd) {
      setTimeout(() => this.setFocusOnTitle());
    }
  }

  setFocusOnTitle() {
    if (this.titleField) {
      const titleElement = ReactDOM.findDOMNode(this.titleField);
      titleElement instanceof HTMLInputElement && titleElement.focus();
    }
  }

  handleCancelClicked() {
    const { pristine, showModalPrompt } = this.props;
    if (!pristine) {
      showModalPrompt({
        message: localizedStrings.FILE_TYPES_PROMPT_DISCARD_CHANGES,
        onButton2: this.resetAndCancel,
        isButton1Default: true,
        isButton2Default: false,
      });
    } else {
      this.resetAndCancel();
    }
  }

  resetAndCancel() {
    const { onCancel, reset } = this.props;
    reset();
    onCancel();
  }

  async handleAddOrUpdateClicked() {
    const {
      extensions,
      fileType,
      fileTypeName: fileTypeNameProp,
      fileTypes: fileTypesProp,
      isAdd,
      setFileTypes,
      showSingleButtonAlert,
      title,
    } = this.props;
    const newFileType: FileType = {
      title: title || '',
      extensions: toExtensionArray(extensions),
      enabled: isAdd || (fileType ? fileType.enabled : false),
    };
    const fileTypes: Array<FileType> = fileTypesProp || [];
    const fileTypeName: string = fileTypeNameProp || '';
    const newName = computeFileTypeName(newFileType).toLowerCase();
    let newFileTypes;
    if (isAdd) {
      if (
        !fileTypes.find(ft => computeFileTypeName(ft).toLowerCase() === newName)
      ) {
        newFileTypes = [...fileTypes, newFileType];
      }
    } else {
      const oldName = fileTypeName.toLowerCase();
      const otherFileTypes: Array<FileType> = fileTypes.filter(
        ft => computeFileTypeName(ft).toLowerCase() !== oldName
      );
      if (
        !otherFileTypes.find(
          ft => computeFileTypeName(ft).toLowerCase() === newName
        )
      ) {
        newFileTypes = [...otherFileTypes, newFileType];
      }
    }
    if (newFileTypes) {
      await setFileTypes(newFileTypes, newFileType);
      this.props.initialize(fileTypeToFormValues(newFileType));
    } else {
      showSingleButtonAlert(localizedStrings.FILE_TYPES_ERROR_NAME_EXISTS, {
        name: newName,
      });
      this.setFocusOnTitle();
    }
  }

  handleKeyDown(e) {
    let keyHandled: boolean = false;
    if (e.key === 'Enter') {
      keyHandled = true;
      this.handleAddOrUpdateClicked();
    } else if (e.key === 'Escape') {
      keyHandled = true;
      this.resetAndCancel();
    }
    if (keyHandled) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  render() {
    const {
      disabled,
      fileType,
      intl: { formatMessage },
      isAdd,
      pristine,
      submitting,
      error,
    } = this.props;

    const addOrUpdateMessage = !!fileType
      ? localizedStrings.FILE_TYPES_UPDATE_FILE_TYPE
      : localizedStrings.FILE_TYPES_ADD_FILE_TYPE;

    // Below: Setting autoComplete to an invalid value seems to be the currently
    // accepted way to turn it off.
    return (
      <div className="filetype-editor" data-test-name="filetypes-editor">
        <FormField
          label={formatMessage(localizedStrings.GLOBAL_SETTINGS_CUSTOM_TITLE)}
          labelClassName="is-normal"
          dataTestName="file-type-custom-name"
        >
          <Field
            className="input"
            component="input"
            id="title"
            name="title"
            type="text"
            disabled={disabled}
            onKeyDown={this.handleKeyDown}
            autoComplete="invalid-autocomplete-value"
            ref={c => (this.titleField = c)}
          />
        </FormField>
        <FormField
          label={formatMessage(localizedStrings.FILE_TYPES_EXTENSIONS)}
          labelClassName="is-normal"
          dataTestName="file-type-extensions"
        >
          <Field
            className="textarea no-resize"
            component="textarea"
            id="extensions"
            name="extensions"
            type="textarea"
            disabled={disabled}
            onKeyDown={this.handleKeyDown}
          />
        </FormField>
        <div className="field is-grouped" style={{ justifyContent: 'center' }}>
          <div className="control">
            <button
              className="button"
              disabled={(!isAdd && pristine) || submitting || disabled}
              onClick={this.handleCancelClicked}
              type="button"
              data-test-name="file-type-cancel"
            >
              <FormattedMessage {...localizedStrings.CANCEL} />
            </button>
          </div>
          <div className="control inline">
            <button
              className="button"
              disabled={pristine || submitting || disabled || error}
              onClick={this.handleAddOrUpdateClicked}
              type="button"
              data-test-name="file-type-add-or-update"
            >
              <FormattedMessage {...addOrUpdateMessage} />
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function toExtensionArray(s: ?string): Array<string> {
  if (!s) {
    return [];
  }
  const a: Array<string> = s.split(',');
  return a.reduce((acc, ex) => {
    ex = ex.trim();
    if (ex) {
      acc.push(ex);
    }
    return acc;
  }, []);
}

function extensionsToString(extensions: Array<string>) {
  return extensions.reduce((acc, s) => {
    if (acc) {
      acc = acc + ',';
    }
    return acc + s;
  }, '');
}

function fileTypeToFormValues(ft: ?FileType): Object {
  if (!ft) {
    return {};
  }
  return {
    title: ft.title || '',
    extensions: extensionsToString(ft.extensions),
  };
}

function validate(data: Object, props: Props) {
  return {
    _error: !data.extensions || !toExtensionArray(data.extensions).length,
  };
}

const FORM_NAME = 'FileTypeEditor';

const form = reduxForm({
  form: 'FileTypeEditor',
  validate,
  onChange: (values, dispatch, props) => {
    props.setHasUnsavedChanges(props.dirty);
  },
})(injectIntl(FileTypeEditor));

const valueSelector = formValueSelector(FORM_NAME);

export default connect(
  state => ({
    title: valueSelector(state, 'title'),
    extensions: valueSelector(state, 'extensions'),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      showModalPrompt,
      showSingleButtonAlert,
    })
)(injectIntl(form));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/FileTypeEditor.js